import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  FileText,
  Clock,
  AlertCircle,
  X,
  MessageCircle,
  Shield,
  Pill,
  Sparkles,
  Gift
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { practiceApi, bustCache } from '../services/authApi';
import LoadingSpinner, { ErrorMessage } from './LoadingSpinner';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';
import { DictateVisitBanner } from './dictation/DictateVisitDialog';
import { UsageAlertBanner } from './dashboard/UsageAlertBanner';
import { PatientMessagesTab } from './patients/PatientMessagesTab';
import { PatientActivityTab } from './patients/PatientActivityTab';
import { PatientEcardsTab } from './patients/PatientEcardsTab';
import { ReferralFollowupReminder } from './referrals/ReferralFollowupReminder';
import ImportPatientsModal from './ImportPatientsModal';
import { PatientListImportDialog } from './patients/PatientListImportDialog';
import { can } from './RequirePermission';
import SupportModal from './SupportModal';
import SupportHistory from './SupportHistory';
import TutorialsModal from './TutorialsModalPortal';
import ExportDataModal from './ExportDataModal';
import { ScanSignedConsentModal } from './ScanSignedConsentModal';
import { WhatsNewModal } from './WhatsNewModal';
import { AnnouncementsAdmin } from './AnnouncementsAdmin';
import TestimonialPromptBanner from './TestimonialPromptBanner';
import { fmtDateUS, safeText, printProcedure, printProblemSheet, printComplianceReport as printComplianceReportFor, printPatientReport as printPatientReportFor } from './dashboard/printReports';
import { DashboardHeader } from './dashboard/DashboardHeader';
import { DashboardSidebar } from './dashboard/DashboardSidebar';
import { MedCardsViewModal } from './dashboard/MedCardsViewModal';
import { DashboardStats } from './dashboard/DashboardStats';
import { PatientsPanel } from './dashboard/PatientsPanel';
import { RecordsHeader } from './dashboard/RecordsHeader';
import { ProceduresTab } from './dashboard/ProceduresTab';
import { ConsentsTab } from './dashboard/ConsentsTab';
import { MedCardsTab } from './dashboard/MedCardsTab';
import { ReferralsTab } from './dashboard/ReferralsTab';
import { ComplianceTab } from './dashboard/ComplianceTab';
import { FollowUpsTab } from './dashboard/FollowUpsTab';
import { ProblemsTab } from './dashboard/ProblemsTab';
import { SmilesTab } from './dashboard/SmilesTab';
import { HipaaTab } from './dashboard/HipaaTab';
import { ProblemViewModal } from './dashboard/ProblemViewModal';
import { SmsRecipientModal } from './dashboard/SmsRecipientModal';

// Print styles for professional patient handouts (copied from MedCardLibraryPage)
const printStyles = `
  @media screen {
    .print-only {
      display: none !important;
    }
  }
  
  @media print {
    @page {
      margin: 0.5in;
      size: letter portrait;
    }
    
    body > *:not(.print-only) {
      display: none !important;
    }
    
    .print-only {
      display: block !important;
      width: 100% !important;
      margin: 0 !important;
      padding: 0 !important;
      background: white !important;
    }
    
    * {
      -webkit-print-color-adjust: exact !important;
      color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    
    .print-header, .border-l-4 {
      page-break-inside: avoid !important;
    }
  }
`;

const MAX_PATIENT_RESULTS = 8;

const PracticeDashboard = () => {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [realPatients, setRealPatients] = useState([]);  // Store real patients separately
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [trialBannerDismissed, setTrialBannerDismissed] = useState(false);

  // GRAY SCREEN KILLER: Detect and destroy any rogue overlay that blocks the dashboard
  useEffect(() => {
    const killOverlays = () => {
      // Never touch overlays that belong to a dialog the user actually opened
      if (document.querySelector('[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]')) return;
      // Find any fixed full-screen overlay with dark background that isn't from an intentionally opened modal
      document.querySelectorAll('[data-state="open"][class*="fixed"][class*="inset-0"]').forEach(el => {
        // Only kill overlays that don't have an associated open modal trigger
        const parent = el.closest('[data-state="open"]');
        if (parent && !document.querySelector('[data-radix-dialog-trigger][data-state="open"]')) {
          el.remove();
        }
      });
    };

    // Run periodically for the first 10 seconds after mount
    const timers = [
      setTimeout(killOverlays, 1000),
      setTimeout(killOverlays, 3000),
      setTimeout(killOverlays, 5000),
      setTimeout(killOverlays, 10000),
    ];

    // Also listen for Escape key to force-kill any overlay
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        document.querySelectorAll('.fixed.inset-0.z-50, [class*="fixed inset-0"][class*="bg-black"]').forEach(el => {
          el.style.display = 'none';
        });
      }
    };
    window.addEventListener('keydown', handleEscape);

    return () => {
      timers.forEach(clearTimeout);
      window.removeEventListener('keydown', handleEscape);
    };
  }, []);
  // Restore selected patient from localStorage if available
  const [selectedPatientId, setSelectedPatientId] = useState(null);
  const [messagePatientOpen, setMessagePatientOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [selectedPatientReferralSource, setSelectedPatientReferralSource] = useState(null);
  const [patientSearchTerm, setPatientSearchTerm] = useState('');
  const [procedureSearchTerm, setProcedureSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('procedures'); // procedures, consents, medcards, referrals, problems, smiles
  const [refreshing, setRefreshing] = useState(false);
  const [scanConsentModal, setScanConsentModal] = useState({ open: false, consent: null });
  const [showAnnouncementsAdmin, setShowAnnouncementsAdmin] = useState(false);
  const [viewMedCardsModal, setViewMedCardsModal] = useState(false);
  const [isEditingMedCards, setIsEditingMedCards] = useState(false);
  const [editedMedCardContent, setEditedMedCardContent] = useState('');
  const [, setViewingAssignment] = useState(null);
  const [viewingCards, setViewingCards] = useState([]);
  const [smileTransformations, setSmileTransformations] = useState([]);
  const [loadingSmiles, setLoadingSmiles] = useState(false);
  const [hipaaConsent, setHipaaConsent] = useState(null);
  const [loadingHipaa, setLoadingHipaa] = useState(false);
  
  // SMS recipient selection state
  const [showSmsModal, setShowSmsModal] = useState(false);
  const [smsModalData, setSmsModalData] = useState(null);
  const [smsRecipient, setSmsRecipient] = useState('patient');
  const [sendingSmsFromModal, setSendingSmsFromModal] = useState(false);
  
  const { user, practice, logout } = useAuth();
  const { toast } = useToast();

  // Show SMS modal with recipient selection
  const showSmsSelectionModal = (patientId, documentType, documentId, patientPhone, patientName, documentName, specialistPhone = null, specialistName = null) => {
    setSmsModalData({
      patientId,
      documentType,
      documentId,
      patientPhone,
      patientName,
      documentName,
      specialistPhone,
      specialistName
    });
    setSmsRecipient('patient');
    setShowSmsModal(true);
  };

  // Send SMS from modal with selected recipient
  const sendSmsFromModal = async () => {
    if (!smsModalData) return;
    
    const { patientId, documentType, documentId, patientPhone, patientName, documentName, specialistPhone, specialistName } = smsModalData;
    
    setSendingSmsFromModal(true);
    
    try {
      const recipients = [];
      
      if (smsRecipient === 'patient' || smsRecipient === 'both') {
        if (!patientPhone) {
          toast({ title: "Error", description: "Patient does not have a phone number", variant: "destructive" });
          setSendingSmsFromModal(false);
          return;
        }
        recipients.push({ phone: patientPhone, name: patientName, type: 'patient' });
      }
      
      if ((smsRecipient === 'specialist' || smsRecipient === 'both') && specialistPhone) {
        recipients.push({ phone: specialistPhone, name: specialistName, type: 'specialist' });
      }
      
      if (recipients.length === 0) {
        toast({ title: "Error", description: "No valid recipient selected", variant: "destructive" });
        setSendingSmsFromModal(false);
        return;
      }
      
      let successCount = 0;
      const token = localStorage.getItem('dentalToken');
      
      for (const recipient of recipients) {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sms/send-document`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            patient_id: patientId,
            document_type: documentType,
            document_id: documentId,
            phone_number: recipient.phone
          })
        });
        
        if (response.ok) {
          successCount++;
        }
      }
      
      if (successCount > 0) {
        toast({
          title: "📱 Text Sent Successfully",
          description: `${documentName} sent to ${recipients.map(r => r.name).join(' and ')}`,
        });
        loadDashboard();
      } else {
        toast({ title: "Error", description: "Failed to send text", variant: "destructive" });
      }
      
      setShowSmsModal(false);
      setSmsModalData(null);
    } catch (error) {
      console.error('SMS error:', error);
      toast({ title: "Error", description: "Failed to send text message", variant: "destructive" });
    } finally {
      setSendingSmsFromModal(false);
    }
  };

  // Load smile transformations when selected patient changes
  useEffect(() => {
    if (selectedPatientId) {
      loadSmileTransformations(selectedPatientId);
    }
  }, [selectedPatientId]);

  // Load smile transformations for a patient
  const loadSmileTransformations = async (patientId) => {
    if (!patientId) return;
    setLoadingSmiles(true);
    try {
      const token = localStorage.getItem('dentalToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/smile-design/transformations/${patientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setSmileTransformations(data.transformations || []);
    } catch (error) {
      console.error('Error loading smile transformations:', error);
      setSmileTransformations([]);
    } finally {
      setLoadingSmiles(false);
    }
  };

  const loadHipaaConsent = async (patientId) => {
    if (!patientId) return;
    setLoadingHipaa(true);
    try {
      const token = localStorage.getItem('dentalToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/portal/hipaa/${patientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setHipaaConsent(data.data || null);
    } catch (error) {
      console.error('Error loading HIPAA consent:', error);
      setHipaaConsent(null);
    } finally {
      setLoadingHipaa(false);
    }
  };

  const [showDatePickerModal, setShowDatePickerModal] = useState(false);
  const [showCSVImportModal, setShowCSVImportModal] = useState(false);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [currentView, setCurrentView] = useState('dashboard'); // dashboard, support
  const [showProblemModal, setShowProblemModal] = useState(false);
  const [selectedProblemForView, setSelectedProblemForView] = useState(null);

  const [showTutorialsModal, setShowTutorialsModal] = useState(false);
  useEffect(() => {
    const open = () => setShowTutorialsModal(true);
    const imp = () => setShowCSVImportModal(true);
    window.addEventListener('practcom:open-tutorials', open);
    window.addEventListener('practcom:open-import-patients', imp);
    return () => { window.removeEventListener('practcom:open-tutorials', open); window.removeEventListener('practcom:open-import-patients', imp); };
  }, []);
  
  // Sidebar state
  const [sidebarOpen, setSidebarOpen] = useState(() => window.innerWidth >= 1024);
  const [expandedSections, setExpandedSections] = useState({
    dailyTasks: true,
    resources: true,
    management: true,
    analytics: true,
    support: true
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleExportData = () => {
    console.log('📤 Export Data button clicked!');
    console.log('📤 Setting showDatePickerModal to TRUE');
    setShowDatePickerModal(true);
    console.log('📤 showDatePickerModal state should now be true');
  };

  const handleImportPatients = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    console.log('🔍 Import Patients button clicked');
    console.log('🔍 Current showCSVImportModal state:', showCSVImportModal);
    // Use functional update to ensure we're setting to true
    setShowCSVImportModal(() => true);
    console.log('🔍 Setting showCSVImportModal to true');
  };

  const handleImportSuccess = (results) => {
    // Reload dashboard silently to show new patients
    loadDashboard(true);
    
    // Close the import modal
    setShowCSVImportModal(false);
    
    toast({
      title: "Import Successful",
      description: `${results.summary.successfulImports} patients have been imported and will appear in Patients.`,
      variant: "default",
    });
  };

  // Load dashboard on mount
  useEffect(() => {
    loadDashboard();
    // Auto-refresh every 60 seconds (silent) so consent signatures etc. appear
    const interval = setInterval(() => {
      loadDashboard(true);
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleOpenProcedure = (procedureId) => {
    // Navigate to procedure details page
    navigate(`/procedure-details/${procedureId}`);
  };

  const handleEditProcedure = (procedureId) => {
    console.log('🔧 handleEditProcedure called with ID:', procedureId);
    // Navigate to procedure details page where they can edit the actual procedure content
    navigate(`/procedure-details/${procedureId}`);
  };

  const handlePrintProcedure = (procedureId) => printProcedure(procedureId, { dashboardData, practice, toast });

  const handleEmailPDF = async (procedureId) => {
    console.log('🔥 EMAIL BUTTON CLICKED:', procedureId);
    console.log('🔍 Dashboard data:', dashboardData);
    console.log('🔍 Available procedures:', dashboardData?.recentProcedures);
    
    try {
      const procedure = dashboardData?.recentProcedures?.find(p => p.id === procedureId);
      if (!procedure) {
        console.error('Procedure not found for email');
        console.log('🔍 Looking for procedureId:', procedureId);
        console.log('🔍 Available procedure IDs:', dashboardData?.recentProcedures?.map(p => p.id));
        return;
      }

      // Try to find patient email from recentPatients data using patientId
      let patientEmail = procedure.patientEmail;
      
      // Find patient data for logging purposes
      const patient = procedure.patientId ? 
        dashboardData?.recentPatients?.find(p => p.id === procedure.patientId) : null;
      
      if (!patientEmail && procedure.patientId) {
        console.log('🔍 Looking for patient email using patientId:', procedure.patientId);
        console.log('🔍 Available recentPatients:', dashboardData?.recentPatients);
        console.log('🔍 Found patient:', patient);
        
        if (patient) {
          patientEmail = patient.email;
          console.log('🔍 Patient email found:', patientEmail);
        }
      }

      if (!patientEmail) {
        toast({
          title: "No Email Address",
          description: "This patient doesn't have an email address on file. Please add one in Patient Management → Edit Patient.",
          variant: "destructive",
          duration: 5000,
        });
        return;
      }

      // Call backend API to send email
      console.log('📧 Calling email API with:', {
        patientEmail: patientEmail,
        procedureId: procedure.id,
        procedureName: procedure.procedureName || procedure.name
      });
      
      const response = await practiceApi.emailPDF({
        patientEmail: patientEmail,
        procedureId: procedure.id,
        procedureName: procedure.procedureName || procedure.name,
        assignmentId: procedure.id,
        patientId: procedure.patientId || selectedPatientId
      });

      console.log('📧 Email API response:', response);

      if (response.success) {
        // Log the email activity
        try {
          await practiceApi.logActivity({
            patientId: procedure.patientId,
            patientName: patient ? `${patient.firstName} ${patient.lastName}` : procedure.patientName || 'Unknown Patient',
            patientEmail: patientEmail,
            procedureId: procedure.id,
            procedureName: procedure.procedureName || procedure.name,
            dentistName: patient?.primaryDentist || procedure.dentistName || 'Unknown Doctor',
            activityType: 'email',
            additionalData: { emailAddress: patientEmail }
          });
        } catch (logError) {
          console.error('Failed to log email activity:', logError);
          // Don't fail the main operation if logging fails
        }

        // Mark as delivered and schedule 24-hour follow-up
        try {
          await handleMarkAsDelivered(procedure.id, procedure.procedureName || procedure.name);
        } catch (deliveredError) {
          console.error('Failed to mark as delivered after email:', deliveredError);
          // Don't fail the main operation if this fails
        }

        // Show toast notification at bottom right
        toast({
          title: "✅ Email Sent Successfully",
          description: `PDF instructions sent to ${patientEmail} - Follow-up scheduled in 24 hours`,
          variant: "default",
        });
        
        // Refresh dashboard to show email sent indicator
        loadDashboard();
      }
      
    } catch (error) {
      console.error('Error sending PDF email:', error);
      toast({
        title: "Email Failed",
        description: error.response?.data?.detail || "Failed to send PDF email",
        variant: "destructive",
      });
    }
  };

  const handleOpenProblemView = (assignmentId) => {
    console.log('🔍 Opening problem details page, assignment ID:', assignmentId);
    navigate(`/problem-details/${assignmentId}`);
  };

  const handleEditProblem = (assignmentId) => {
    console.log('✏️ Edit problem details, assignment ID:', assignmentId);
    navigate(`/problem-details/${assignmentId}`);
  };

  const handlePrintProblemById = async (assignmentId) => {
    try {
      console.log('🖨️ Printing problem, assignment ID:', assignmentId);
      const problem = dashboardData?.problemsEmergencies?.find(p => p.id === assignmentId);
      if (!problem) {
        console.error('Problem assignment not found');
        toast({
          title: "Error",
          description: "Problem/Emergency not found",
          variant: "destructive"
        });
        return;
      }

      // Fetch full problem details including overview content
      const token = localStorage.getItem('dentalToken');
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/problems-emergencies/library/${problem.problemId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data?.success && response.data?.data) {
        const fullProblemData = response.data.data;
        console.log('📋 Full problem data fetched:', fullProblemData);
        
        // Call the print function with the full problem data
        handlePrintProblem(fullProblemData);
      } else {
        throw new Error('Failed to fetch problem details');
      }
    } catch (error) {
      console.error('❌ Error printing problem:', error);
      toast({
        title: "Print Failed",
        description: error.response?.data?.detail || "Failed to print problem/emergency",
        variant: "destructive"
      });
    }
  };

  const handleEmailProblem = async (assignmentId) => {
    try {
      console.log('📧 Emailing problem, assignment ID:', assignmentId);
      const problem = dashboardData?.problemsEmergencies?.find(p => p.id === assignmentId);
      if (!problem) {
        console.error('Problem assignment not found');
        toast({
          title: "Error",
          description: "Problem/Emergency not found",
          variant: "destructive"
        });
        return;
      }

      const patient = dashboardData?.recentPatients?.find(p => p.id === selectedPatientId);
      if (!patient?.email) {
        toast({
          title: "No Email Address",
          description: "This patient doesn't have an email address on file. Please add one in Patient Management → Edit Patient.",
          variant: "destructive",
          duration: 5000,
        });
        return;
      }

      console.log('📋 Problem:', problem);
      console.log('👤 Patient:', patient);

      const token = localStorage.getItem('dentalToken');
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/problems-emergencies/email`,
        {
          problemId: problem.problemId,
          patientEmail: patient.email,
          patientName: `${patient.firstName} ${patient.lastName}`,
          patientId: selectedPatientId
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log('✅ Email sent successfully');
      toast({
        title: "Email Sent!",
        description: `Problem/Emergency instructions sent to ${patient.email}`
      });
      
      // Reload dashboard to update email status
      loadDashboard();
    } catch (error) {
      console.error('❌ Error sending problem email:', error);
      toast({
        title: "Email Failed",
        description: error.response?.data?.detail || "Failed to send email",
        variant: "destructive"
      });
    }
  };

  const handlePrintProblem = (problemData) => printProblemSheet(problemData, { practice, toast });

  // SMS functionality removed - no longer available

  const handlePatientClick = async (patientId) => {
    console.log('👤 Patient clicked:', patientId, 'Current selection:', selectedPatientId);
    // Always select the patient (don't deselect if same patient clicked)
    console.log('👤 Selecting patient:', patientId);
    setSelectedPatientId(patientId);
    
    // Fetch the patient's inbound referral source if they have one
    setSelectedPatientReferralSource(null); // Reset while loading
    const patient = dashboardData?.recentPatients?.find(p => p.id === patientId);
    if (patient?.inboundReferralSourceId) {
      try {
        const token = localStorage.getItem('dentalToken');
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/inbound-referrals/sources/${patient.inboundReferralSourceId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setSelectedPatientReferralSource(data.source);
          console.log('📥 Loaded referral source:', data.source?.name);
        }
      } catch (err) {
        console.error('Error loading referral source:', err);
      }
    }
    
    // DO NOT clear search - keep patient visible in the list
    // User can manually clear search if needed
    
    // Automatically load patient records by expanding the patient records section
    // This ensures the patient's procedures/consents show immediately
    setExpandedSections(prev => ({
      ...prev,
      patientManagement: true  // Ensure Patient Records section is expanded
    }));
  };

  const getSelectedPatient = () =>
    realPatients.find((p) => p.id === selectedPatientId) ||
    dashboardData?.recentPatients?.find((p) => p.id === selectedPatientId) ||
    {};
  const printComplianceReport = (forms) => printComplianceReportFor(forms, { patient: getSelectedPatient(), practice, toast });

  const printPatientReport = (config) => printPatientReportFor(config, { patient: getSelectedPatient(), practice, toast });


  const handleMarkAsDelivered = async (procedureId, procedureName) => {
    try {
      console.log('📦 Marking procedure as delivered:', procedureId, procedureName);
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/practice/assignment/${procedureId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('dentalToken')}`
        },
        body: JSON.stringify({ status: 'delivered' })
      });

      if (response.ok) {
        // Reload dashboard to update the count and status
        loadDashboard();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update status');
      }
    } catch (error) {
      console.error('❌ Error marking as delivered:', error);
      toast({
        title: "Error",
        description: `Failed to mark ${procedureName} as delivered: ${error.message}`,
        variant: "destructive",
      });
    }
  };

  // Filter and sort real patients only - search by patient name or email
  const filteredRealPatients = realPatients?.filter(patient => {
    if (patientSearchTerm) {
      const searchLower = patientSearchTerm.toLowerCase();
      return (
        patient.firstName?.toLowerCase().includes(searchLower) ||
        patient.lastName?.toLowerCase().includes(searchLower) ||
        patient.email?.toLowerCase().includes(searchLower) ||
        searchLower.split(/\s+/).filter(Boolean).every((tok) => `${patient.firstName || ''} ${patient.lastName || ''} ${patient.email || ''}`.toLowerCase().includes(tok))
      );
    }
    return false; // Only show search results — never the whole list
  })
  .sort((a, b) => {
    // Sort by last name alphabetically
    return a.lastName.localeCompare(b.lastName);
  }) || [];

  const shownPatients = patientSearchTerm
    ? filteredRealPatients.slice(0, MAX_PATIENT_RESULTS)
    : realPatients.filter((p) => p.id === selectedPatientId);

  // Language demographics computed from patient data
  const languageDemographics = useMemo(() => {
    const LANGUAGE_NAMES = {
      en: 'English', es: 'Spanish', zh: 'Chinese', vi: 'Vietnamese',
      ko: 'Korean', pt: 'Portuguese', fr: 'French', ar: 'Arabic',
      ru: 'Russian', hi: 'Hindi', ja: 'Japanese', de: 'German',
      it: 'Italian', pl: 'Polish', tl: 'Tagalog', he: 'Hebrew'
    };
    const LANG_COLORS = [
      'bg-blue-500', 'bg-emerald-500', 'bg-amber-500', 'bg-rose-500',
      'bg-violet-500', 'bg-cyan-500', 'bg-orange-500', 'bg-pink-500'
    ];

    if (!realPatients?.length) return { total: 0, nonEnglish: 0, breakdown: [] };

    const counts = {};
    realPatients.forEach(p => {
      const lang = p.preferredLanguage || 'en';
      counts[lang] = (counts[lang] || 0) + 1;
    });

    const total = realPatients.length;
    const nonEnglish = total - (counts['en'] || 0);
    const breakdown = Object.entries(counts)
      .filter(([code]) => code !== 'en')
      .sort((a, b) => b[1] - a[1])
      .map(([code, count], i) => ({
        code,
        name: LANGUAGE_NAMES[code] || code,
        count,
        pct: Math.round((count / total) * 100),
        color: LANG_COLORS[i % LANG_COLORS.length]
      }));

    return { total, nonEnglish, englishCount: counts['en'] || 0, breakdown };
  }, [realPatients]);

  // Filter procedures based on selected patient and search term
  // Only show procedures when a patient is selected
  const filteredProcedures = selectedPatientId 
    ? (dashboardData?.recentProcedures?.filter(procedure => {
        // Filter by selected patient
        if (procedure.patientId !== selectedPatientId) {
          return false;
        }
        
        // Filter by search term
        if (procedureSearchTerm) {
          const searchLower = procedureSearchTerm.toLowerCase();
          return (
            procedure.procedureName.toLowerCase().includes(searchLower) ||
            procedure.dentistName.toLowerCase().includes(searchLower) ||
            (procedure.patientName && procedure.patientName.toLowerCase().includes(searchLower))
          );
        }
        
        return true;
      }) || [])
    : [];

  // Filter informed consents based on selected patient and search term
  // Only show consents when a patient is selected
  const filteredConsents = selectedPatientId
    ? (dashboardData?.recentInformedConsents?.filter(consent => {
        // Filter by selected patient
        if (consent.patientId !== selectedPatientId) {
          return false;
        }
        
        // Filter by search term (using procedure search term for now)
        if (procedureSearchTerm) {
          const searchLower = procedureSearchTerm.toLowerCase();
          return (
            consent.consentName.toLowerCase().includes(searchLower) ||
            consent.dentistName.toLowerCase().includes(searchLower) ||
            (consent.patientName && consent.patientName.toLowerCase().includes(searchLower))
          );
        }
        
        return true;
      }) || [])
    : [];

  // Split for the two distinct tabs:
  //   - "Informed Consents" tab → only standard informed consents
  //   - "Compliance Forms"  tab → Patient Treatment Refusal + X-Ray + Periodontal + Failure-to-Complete
  const consentsOnly = filteredConsents.filter(
    c => !c.isRefusalForm && !c.isXrayRefusalForm && !c.isPerioRefusalForm && !c.isFailureToCompleteForm && !c.isPatientComplianceForm
  );
  const complianceOnly = filteredConsents.filter(
    c => c.isRefusalForm || c.isXrayRefusalForm || c.isPerioRefusalForm || c.isFailureToCompleteForm || c.isPatientComplianceForm
  );

  // Post-treatment check-in follow-ups for the selected patient
  const [patientFollowUps, setPatientFollowUps] = useState([]);
  useEffect(() => {
    if (!selectedPatientId) {
      setPatientFollowUps([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/follow-ups/patient/${selectedPatientId}`,
          { headers: { Authorization: `Bearer ${localStorage.getItem('dentalToken')}` } }
        );
        if (!cancelled) setPatientFollowUps(res.data?.data || []);
      } catch {
        if (!cancelled) setPatientFollowUps([]);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedPatientId, activeTab]);

  // Filter med cards based on selected patient
  const filteredMedCards = selectedPatientId
    ? (dashboardData?.recentMedCards || []).filter(medCard => {
        return medCard.patientId === selectedPatientId;
      })
    : [];

  // Filter referrals based on selected patient
  const filteredReferrals = selectedPatientId
    ? (dashboardData?.recentReferrals || []).filter(referral => {
        return referral.patientId === selectedPatientId;
      })
    : [];

  const filteredProblems = selectedPatientId
    ? (dashboardData?.problemsEmergencies || []).filter(problem => {
        return problem.patientId === selectedPatientId;
      })
    : [];

  const loadDashboard = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      else bustCache('dashboard');
      setError(null);
      
      const dashboardResponse = await practiceApi.getDashboard();
      
      setDashboardData(dashboardResponse.data);
      
      
      // Dashboard already returns all patients — no need for a separate getPatients call
      const allPatients = dashboardResponse.data?.recentPatients || [];
      
      const allPatientsWithStatus = allPatients.map(patient => ({
        ...patient,
        status: patient.isActive !== false ? 'Active' : 'Inactive'
      }));
      
      setRealPatients(allPatientsWithStatus);
      
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load dashboard');
      toast({
        title: "Error",
        description: "Failed to load dashboard data",
        variant: "destructive",
      });
    } finally {
      if (!silent) setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="flex justify-center items-center py-32">
            <LoadingSpinner size="xl" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <ErrorMessage message={error} onRetry={loadDashboard} />
        </div>
      </div>
    );
  }

  const getSubscriptionStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'trial': return 'bg-blue-100 text-blue-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <>
      {/* What's New / Announcements Modal */}
      {user?.welcomeVideoSeen !== false && <WhatsNewModal />}
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex flex-col" style={window.innerWidth >= 1024 ? {
      transform: 'scale(0.75)',
      transformOrigin: 'top left',
      width: '133.33%',
      height: '133.33%'
    } : undefined}>
      <style>{printStyles}</style>
      
      {/* Header */}
      <DashboardHeader {...{ dashboardData, getSubscriptionStatusColor, logout, practice, setSidebarOpen, sidebarOpen }} />

      {/* Main Content Area with Sidebar */}
      <div className="flex flex-1 overflow-hidden">
        
        {/* Sidebar with smooth transitions - Fixed height and scroll - Doubled width for better readability */}
<DashboardSidebar {...{ currentView, expandedSections, handleExportData, handleImportPatients, importOpen, loadDashboard, navigate, practice, setCurrentView, setImportOpen, setShowAnnouncementsAdmin, setShowSupportModal, setShowTutorialsModal, sidebarOpen, setSidebarOpen, toggleSection, user }} />

        {/* Main Dashboard Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="w-full px-3 py-4">

      {/* Tutorials Modal */}
      <TutorialsModal
        isOpen={showTutorialsModal}
        onClose={() => setShowTutorialsModal(false)}
      />

      {/* View Med Cards Modal - EXACT copy from MedCardLibraryPage */}
      <MedCardsViewModal {...{ editedMedCardContent, error, isEditingMedCards, loadDashboard, practice, setEditedMedCardContent, setIsEditingMedCards, setViewMedCardsModal, toast, viewMedCardsModal, viewingCards }} />

        {currentView === 'dashboard' && (
          <>
            {/* Founder testimonial prompt — only appears after 3+ logins, dismissible */}
            <TestimonialPromptBanner
              user={{ ...(user || {}), practice, practiceId: practice?.id }}
              token={localStorage.getItem('dentalToken')}
            />

            {/* Trial Countdown Banner (Days 1-12) — hands off to TrialEndingBanner on Days 13-15 */}
            {(() => {
              const sub = practice?.subscription || {};
              const source = practice?.source;
              // Hide for: SamCart purchases, active subs, beta testers, lifetime/professional plans
              const isPaid = source === 'samcart' || sub.status === 'active' || sub.status === 'beta' || sub.plan === 'lifetime' || sub.plan === 'professional';
              const isTrial = sub.status === 'trial';
              const isExpired = sub.status === 'trial_expired';
              if (isPaid || trialBannerDismissed || (!isTrial && !isExpired)) return null;

              // Expired state handled by the blocking TrialExpiredGate; skip inline banner.
              if (isExpired) return null;

              // Prefer new trialEndsAt field; fall back to legacy trialEndDate for older accounts.
              const endField = sub.trialEndsAt || sub.trialEndDate;
              let daysLeft = 0;
              if (endField) {
                daysLeft = Math.max(0, Math.ceil((new Date(endField) - new Date()) / (1000 * 60 * 60 * 24)));
              }

              // Final 3 days → TrialEndingBanner (sticky, non-dismissible) takes over.
              if (daysLeft <= 3) return null;

              return (
                <div className="mb-4 p-3 rounded-xl flex items-center justify-between gap-4 bg-slate-50 border border-slate-200" data-testid="trial-banner">
                  <div className="flex items-center gap-3 min-w-0">
                    <Clock className="w-4 h-4 flex-shrink-0 text-slate-500" />
                    <p className="text-sm text-slate-600">
                      <span className="font-bold">{daysLeft} day{daysLeft !== 1 ? 's' : ''} left</span> in your free trial
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <a href="/plans-billing" data-testid="subscribe-now-btn"
                      className="px-3 py-1 text-sm text-blue-600 font-medium hover:underline whitespace-nowrap">
                      Subscribe
                    </a>
                    <button onClick={() => { sessionStorage.setItem('dacn_trial_banner_dismissed', 'true'); setTrialBannerDismissed(true); }}
                      className="p-1 transition-colors text-slate-300 hover:text-slate-500"
                      data-testid="dismiss-trial-banner">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              );
            })()}

            <UsageAlertBanner />
            <DictateVisitBanner onDone={() => loadDashboard(true)} />
            <ReferralFollowupReminder />

            {/* Stats Cards */}
            <DashboardStats {...{ dashboardData, languageDemographics }} />

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(280px,1fr)_minmax(0,2fr)] gap-4">
          {/* Patients */}
          <PatientsPanel {...{ MAX_PATIENT_RESULTS, filteredRealPatients, handlePatientClick, patientSearchTerm, realPatients, selectedPatientId, setPatientSearchTerm, shownPatients }} />

          {/* Procedures / Informed Consents / Med Cards / Referrals */}
          <Card className="md:pr-20">
            <RecordsHeader {...{ activeTab, complianceOnly, dashboardData, loadDashboard, loadHipaaConsent, loadSmileTransformations, messagePatientOpen, patientFollowUps, realPatients, refreshing, selectedPatientId, setActiveTab, setMessagePatientOpen, setRefreshing, toast }} />
            <CardContent>
              {!selectedPatientId ? (
                <div className="text-center py-12 text-gray-500">
                  <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-lg font-medium mb-2">Select a Patient</p>
                  <p className="text-sm">Choose a patient from the list above to view their records</p>
                </div>
              ) : (
                <>
                  {/* Referral From Banner - Show if patient was referred by someone */}
                  {selectedPatientReferralSource && (
                    <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <Gift className="h-5 w-5 text-purple-600 mr-2" />
                          <span className="text-sm font-medium text-purple-700">Referred By:</span>
                          <span className="ml-2 font-semibold text-purple-900">{selectedPatientReferralSource.name}</span>
                          <Badge className="ml-2 bg-purple-100 text-purple-700 text-xs">
                            {selectedPatientReferralSource.sourceType}
                          </Badge>
                        </div>
                        {(selectedPatientReferralSource.email || selectedPatientReferralSource.phone) && (
                          <div className="text-xs text-purple-600">
                            {selectedPatientReferralSource.email && <span className="mr-3">{selectedPatientReferralSource.email}</span>}
                            {selectedPatientReferralSource.phone && <span>{selectedPatientReferralSource.phone}</span>}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {activeTab === 'procedures' && filteredProcedures.length > 0 ? (
                <ProceduresTab {...{ dashboardData, filteredProcedures, fmtDateUS, handleEditProcedure, handleEmailPDF, handleOpenProcedure, handlePrintProcedure, printPatientReport, safeText, showSmsSelectionModal }} />
              ) : activeTab === 'consents' && consentsOnly.length > 0 ? (
                <ConsentsTab {...{ consentsOnly, dashboardData, error, fmtDateUS, loadDashboard, navigate, printPatientReport, safeText, setScanConsentModal, showSmsSelectionModal, toast }} />
              ) : activeTab === 'medcards' && filteredMedCards.length > 0 ? (
                <MedCardsTab {...{ dashboardData, error, filteredMedCards, fmtDateUS, loadDashboard, practice, printPatientReport, safeText, setEditedMedCardContent, setIsEditingMedCards, setViewMedCardsModal, setViewingAssignment, setViewingCards, showSmsSelectionModal, toast }} />
              ) : activeTab === 'referrals' ? (
                <ReferralsTab {...{ dashboardData, error, filteredReferrals, fmtDateUS, loadDashboard, navigate, practice, printPatientReport, safeText, selectedPatientId, selectedPatientReferralSource, showSmsSelectionModal, toast, user }} />
              ) : activeTab === 'consents' ? (
                <div className="text-center py-8 text-gray-500">
                  <Shield className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p>No informed consents found for this patient</p>
                  <Button
                    onClick={() => navigate('/assign-informed-consent')}
                    className="mt-2 bg-indigo-600 hover:bg-indigo-700"
                    size="sm"
                  >
                    Assign Informed Consent
                  </Button>
                </div>
              ) : activeTab === 'compliance' && complianceOnly.length > 0 ? (
                <ComplianceTab {...{ complianceOnly, dashboardData, error, loadDashboard, printComplianceReport, showSmsSelectionModal, toast }} />
              ) : activeTab === 'compliance' ? (
                <div className="text-center py-8 text-gray-500" data-testid="compliance-tab-empty">
                  <Shield className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p>No compliance forms (X-Ray Refusal or Treatment Refusal) found for this patient</p>
                  <Button
                    onClick={() => navigate('/assign-compliance-form')}
                    className="mt-2 bg-amber-600 hover:bg-amber-700"
                    size="sm"
                    data-testid="compliance-empty-assign-btn"
                  >
                    Assign Compliance Form
                  </Button>
                </div>
              ) : activeTab === 'followups' && patientFollowUps.length > 0 ? (
                <FollowUpsTab {...{ fmtDateUS, patientFollowUps, printPatientReport, safeText }} />
              ) : activeTab === 'followups' ? (
                <div className="text-center py-8 text-gray-500" data-testid="followups-tab-empty">
                  <MessageCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p>No post-treatment check-ins yet for this patient</p>
                  <p className="text-xs text-gray-400 mt-2">
                    A check-in is scheduled automatically 24 hours after each post-treatment instruction is sent.
                  </p>
                </div>
              ) : activeTab === 'medcards' ? (
                <div className="text-center py-8 text-gray-500">
                  <Pill className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p>No Med Cards assigned to this patient yet</p>
                  <Button
                    onClick={() => navigate('/med-cards/assign')}
                    className="mt-2 bg-orange-600 hover:bg-orange-700"
                    size="sm"
                  >
                    Assign Med Cards
                  </Button>
                </div>
              ) : activeTab === 'problems' && filteredProblems.length > 0 ? (
                <ProblemsTab {...{ filteredProblems, handleEditProblem, handleEmailProblem, handleOpenProblemView, handlePrintProblemById }} />
              ) : activeTab === 'problems' ? (
                <div className="text-center py-8 text-gray-500">
                  <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p>No problems/emergencies assigned to this patient yet</p>
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate('/assign-problems-emergencies');
                    }}
                    className="mt-2 bg-red-600 hover:bg-red-700"
                    size="sm"
                  >
                    Assign Problem/Emergency
                  </Button>
                </div>
              ) : activeTab === 'smiles' && smileTransformations.length > 0 ? (
                <SmilesTab {...{ dashboardData, error, loadSmileTransformations, practice, selectedPatientId, showSmsSelectionModal, smileTransformations, toast, user }} />
              ) : activeTab === 'smiles' ? (
                <div className="text-center py-8 text-gray-500">
                  <Sparkles className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p>No smile transformations for this patient yet</p>
                  <Button
                    onClick={() => window.location.href = '/smile-design'}
                    className="mt-2 bg-purple-600 hover:bg-purple-700"
                    size="sm"
                  >
                    Create Smile Transformation
                  </Button>
                </div>
              ) : activeTab === 'ecards' ? (
                <PatientEcardsTab patientId={selectedPatientId} />
              ) : activeTab === 'messages' ? (
                <PatientMessagesTab patientId={selectedPatientId} />
              ) : activeTab === 'activity' ? (
                <PatientActivityTab patientId={selectedPatientId} />
              ) : activeTab === 'hipaa' ? (
                <HipaaTab {...{ hipaaConsent, loadingHipaa }} />
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p>No procedures found for this patient</p>
                  <Button
                    onClick={() => navigate('/assign-procedure')}
                    className="mt-2"
                    size="sm"
                  >
                    Assign Procedure
                  </Button>
                </div>
              )}
                </>
              )}
            </CardContent>
          </Card>

        </div>
          </>
        )}

        {/* Support History Section */}
        {currentView === 'support' && (
          <div className="mt-8">
            <SupportHistory onBackToDashboard={() => setCurrentView('dashboard')} />
          </div>
        )}
          </div>
        </div>
      </div>

      {/* Copyright Footer */}
      <div className="bg-white border-t border-gray-200 py-4">
        <div className="max-w-7xl mx-auto px-4">
          <p className="text-center text-sm text-gray-600">
            Copyright © The Oncall Bot LLC 2025
          </p>
        </div>
      </div>
      
      {/* Export Data Modal - Using React Portal */}
      <ExportDataModal 
        isOpen={showDatePickerModal} 
        onClose={() => setShowDatePickerModal(false)} 
      />

      {/* Support Modal */}
      <SupportModal
        isOpen={showSupportModal}
        onClose={() => setShowSupportModal(false)}
        practiceData={practice}
      />

      {/* Import Patients Modal */}
      <ImportPatientsModal
        isOpen={showCSVImportModal}
        onClose={() => setShowCSVImportModal(false)}
        onSuccess={handleImportSuccess}
        canBulk={can(user, 'settings')}
        onPhotoImport={() => setImportOpen(true)}
      />
      <PatientListImportDialog open={importOpen} onOpenChange={setImportOpen} onImported={() => loadDashboard(true)} />

      {/* Problem View Modal */}
      <ProblemViewModal {...{ handlePrintProblem, selectedProblemForView, setSelectedProblemForView, setShowProblemModal, showProblemModal }} />

      {/* SMS Recipient Selection Modal */}
      <SmsRecipientModal {...{ sendSmsFromModal, sendingSmsFromModal, setShowSmsModal, setSmsModalData, setSmsRecipient, showSmsModal, smsModalData, smsRecipient }} />

    </div>

    {/* Scan Signed Consent Modal */}
    <ScanSignedConsentModal
      isOpen={scanConsentModal.open}
      onClose={() => setScanConsentModal({ open: false, consent: null })}
      consent={scanConsentModal.consent}
      onSuccess={() => loadDashboard()}
      toast={toast}
    />

    {/* Announcements Admin */}
    <AnnouncementsAdmin
      isOpen={showAnnouncementsAdmin}
      onClose={() => setShowAnnouncementsAdmin(false)}
      toast={toast}
    />

    </>
  );
};

export default PracticeDashboard;